import { useTranslations } from "@jugl-web/utils";
import { isoToLocalDate } from "@jugl-web/utils/date-time/isoToLocalDate";
import { FC, useMemo } from "react";
import { CustomerTaskModel } from "@jugl-web/rest-api/tasks/models/CustomerTask";
import { BoardAvatar } from "@jugl-web/ui-components";
import {
  PILL_BASE_BG_COLOR,
  PILL_BASE_TEXT_COLOR,
} from "@jugl-web/ui-components/cross-platform/tasks/TaskCardPill";
import { TaskCardField } from "../../components/TaskCard";
import { getBoardFieldColors, isTaskOverdue } from "../../utils";
import { isTaskFormBased } from "../../utils/isTaskFormBased";
import { AdaptedTaskCardProps, GenericTaskCardProviderProps } from "./types";

export const CustomerTaskCardProvider: FC<
  GenericTaskCardProviderProps<CustomerTaskModel>
> = ({ task, children }) => {
  const { t } = useTranslations();

  const fields = useMemo(() => {
    const taskFields: TaskCardField[] = [];

    if (task.board) {
      taskFields.push({
        id: "board",
        startIcon: (
          <BoardAvatar
            name={task.board.name}
            color={task.board.color}
            size="xs"
          />
        ),
        value: task.board.name,
        hint: t(
          {
            id: "tasks-page.board-with-name",
            defaultMessage: "Board: {name}",
          },
          { name: <b>{task.board.name}</b> }
        ),
        ...getBoardFieldColors(task.board.color),
      });
    }

    taskFields.push({
      id: "status",
      value: task.status,
      hint: t(
        {
          id: "tasks-page.status-with-name",
          defaultMessage: "Status: {name}",
        },
        { name: <b>{task.status}</b> }
      ),
      textColor: PILL_BASE_TEXT_COLOR,
      backgroundColor: PILL_BASE_BG_COLOR,
    });

    return taskFields;
  }, [t, task.board, task.status]);

  const props = useMemo<AdaptedTaskCardProps>(
    () => ({
      taskId: task.id,
      title: task.name,
      dueDate: task.due_at ? isoToLocalDate(task.due_at) : null,
      isOverdue: isTaskOverdue(task),
      isFormBased: isTaskFormBased(task),
      shortId: task.order_id || undefined,
      fields,
    }),
    [fields, task]
  );

  return children(props);
};
